import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSession } from '../../state/useSession';
import { trackPixel } from '../../analytics/metaPixel';
import { returnTrip } from '../../shell/returnTo';
import { Button, Screen, TextField } from '../../ui';
import { takePassword } from './heldPassword';

/**
 * What Confirm is handed: the email, where the person was going, and — from Sign in's "not
 * confirmed" and Sign up's "already exists" — `resend`, because the code from last time may be
 * long gone and a fresh one is what they need.
 */
export interface ConfirmState {
  email?: string;
  from?: string;
  resend?: boolean;
}

/**
 * Whether Cognito refused because the account is ALREADY confirmed — to a resend ("User is already
 * confirmed.", InvalidParameterException) or to a code ("… Current status is CONFIRMED",
 * NotAuthorizedException). Read from the message, because both codes also mean other things: an
 * InvalidParameterException for a malformed address must not send anybody to Sign in.
 */
function alreadyConfirmed(err: unknown): boolean {
  const message = err instanceof Error ? err.message : '';
  return /already confirmed|current status is confirmed/i.test(message);
}

export default function Confirm() {
  const { confirmSignUp, resendConfirmation, signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const arrived = location.state as ConfirmState | null;
  const emailFromSignUp = arrived?.email ?? '';
  // Where sign-up was sending them; the sign-in after this finishes the trip (see SignUp).
  const from = arrived?.from;

  /*
   * Sign up's "already exists" covers a confirmed account too, and its "Confirm your email" sent a
   * code on arrival that Cognito refused with the raw "User is already confirmed." — on a code
   * screen with no way to Sign in (review of F7, 2026-09-30). There is nothing to confirm: Sign in,
   * email filled, still on the way to wherever they were going.
   */
  function toSignIn(to: string): void {
    navigate('/sign-in', { replace: true, state: { email: to, confirmed: true, ...(from ? { from } : {}) } });
  }

  const [email, setEmail] = useState(emailFromSignUp);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /*
   * Resend had no busy state, so every press sent another code — and Cognito limits how many it
   * will send, so the fourth press was refused while the first three codes raced each other.
   */
  const [resending, setResending] = useState(false);
  const resendingNow = useRef(false);

  async function resend(to: string = email.trim()): Promise<void> {
    if (resendingNow.current || !to) return;
    resendingNow.current = true;
    setResending(true);
    setError(null);
    setNotice(null);
    try {
      await resendConfirmation(to);
      setNotice(`We sent a new code to ${to}.`);
    } catch (err) {
      if (alreadyConfirmed(err)) {
        toSignIn(to);
        return;
      }
      setError(err instanceof Error ? err.message : 'Could not send another code.');
    } finally {
      resendingNow.current = false;
      setResending(false);
    }
  }

  // Sent for them, once, when they came here because the code was never used.
  const resentOnArrival = useRef(false);
  useEffect(() => {
    if (!arrived?.resend || !emailFromSignUp || resentOnArrival.current) return;
    resentOnArrival.current = true;
    void resend(emailFromSignUp);
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await confirmSignUp(email.trim(), code.trim());
    } catch (err) {
      if (alreadyConfirmed(err)) {
        toSignIn(email.trim());
        return;
      }
      setError(err instanceof Error ? err.message : 'That code was not accepted.');
      setBusy(false);
      return;
    }
    // Here, not at sign-up: an unconfirmed account is not a registration.
    trackPixel('CompleteRegistration');
    /*
     * Signed in with the password they typed moments ago, and on to where they were going. The
     * funnel used to end on an empty Sign in form here, asking for the email and the password
     * again (F7, 2026-09-30). Held in memory by Sign up or Sign in, and only for this email; a
     * reload drops it, and then — as when this sign-in fails for any reason — Sign in is asked
     * with the email already filled.
     */
    const password = takePassword(email);
    if (password) {
      try {
        await signIn(email.trim(), password);
        const trip = returnTrip(from);
        navigate(trip.to, { replace: true, ...(trip.state ? { state: trip.state } : {}) });
        return;
      } catch {
        /* asked to sign in instead, below */
      }
    }
    setBusy(false);
    navigate('/sign-in', { replace: true, state: { email: email.trim(), ...(from ? { from } : {}) } });
  }

  return (
    <Screen title="Check your email">
      <p className="type-body">Enter the code we sent you.</p>
      <form onSubmit={submit} noValidate>
        {!emailFromSignUp && (
          <TextField label="Email" type="email" autoComplete="email" value={email} onChangeText={setEmail} />
        )}
        <TextField
          label="Code"
          value={code}
          onChangeText={setCode}
          placeholder="123456"
          autoComplete="one-time-code"
          inputMode="numeric"
        />
        {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
        {notice && <p role="status" className="type-caption">{notice}</p>}
        <Button title="Confirm" type="submit" loading={busy} />
      </form>
      <Button title="Resend code" variant="ghost" loading={resending} onPress={() => void resend()} />
    </Screen>
  );
}
